import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { downloadXlsx } from "@/lib/download";
import { X, Search, Loader2, FileSpreadsheet, TrendingUp, CheckCircle2, AlertTriangle } from "lucide-react";

const fmtEur = (n) => `${(Number(n) || 0).toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const fmtPct = (n) => `${(Number(n) || 0).toFixed(1)}%`;

/**
 * Matriz global Molécula × Laboratorio.
 * Filas: cada G.H. dispensado; Columnas: laboratorios.
 * Celda: descuento% aplicable (verde si es el mejor, azul si es el actual).
 */
export default function MoleculeLabMatrixModal({ dispId, onClose }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState("");
  const [labFilter, setLabFilter] = useState("");
  const [onlyOpportunities, setOnlyOpportunities] = useState(false);
  const [onlyUncovered, setOnlyUncovered] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get(`/dispensations/${dispId}/molecule-lab-matrix`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "Error cargando matriz"))
      .finally(() => setLoading(false));
  }, [dispId]);

  const filteredMolecules = useMemo(() => {
    if (!data) return [];
    let rows = data.molecules;
    const q = query.trim().toLowerCase();
    if (q) rows = rows.filter((m) => m.gh.toLowerCase().includes(q));
    if (labFilter) {
      rows = rows.filter((m) => {
        const c = m.cells[labFilter];
        return c && c.discount_pct > 0;
      });
    }
    if (onlyOpportunities) rows = rows.filter((m) => (m.upside_eur || 0) > 0);
    if (onlyUncovered) rows = rows.filter((m) => (m.coverage_count || 0) === 0);
    return rows;
  }, [data, query, labFilter, onlyOpportunities, onlyUncovered]);

  const totalUpside = useMemo(() => {
    if (!data) return 0;
    return data.molecules.reduce((s, m) => s + (m.upside_eur || 0), 0);
  }, [data]);

  const uncoveredCount = useMemo(() => {
    if (!data) return 0;
    return data.molecules.filter((m) => (m.coverage_count || 0) === 0).length;
  }, [data]);

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-stretch justify-stretch" data-testid="molecule-lab-matrix-modal">
      <div className="bg-white w-full h-full flex flex-col">
        {/* Header */}
        <div className="border-b border-slate-200 px-6 py-4 flex items-center justify-between flex-shrink-0">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-700 font-bold mb-0.5">Vista global · negociación</div>
            <h2 className="font-display text-2xl font-bold tracking-tight text-slate-950">
              Matriz Molécula × Laboratorio
            </h2>
            <div className="text-xs text-slate-500 mt-0.5">
              {data ? `${data.molecules.length} moléculas · ${data.labs.length} laboratorios` : "Cargando…"}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => downloadXlsx(`/dispensations/${dispId}/molecule-lab-matrix/export`, "matriz_molecula_lab.xlsx")}
              className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold px-3.5 py-2 rounded-lg"
              data-testid="matrix-export-xlsx"
            >
              <FileSpreadsheet className="h-4 w-4" /> Exportar Excel
            </button>
            <button
              onClick={onClose}
              className="p-2 rounded-md hover:bg-slate-100 text-slate-500"
              data-testid="matrix-close"
              aria-label="Cerrar"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Toolbar */}
        <div className="border-b border-slate-200 px-6 py-3 flex flex-wrap gap-3 items-center flex-shrink-0 bg-slate-50">
          <div className="flex items-center gap-2 px-3 py-2 bg-white rounded-md border border-slate-200 min-w-[240px]">
            <Search className="h-4 w-4 text-slate-400" />
            <input
              type="text"
              placeholder="Buscar molécula (G.H.)"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="flex-1 bg-transparent outline-none text-sm"
              data-testid="matrix-search"
            />
          </div>
          <select
            value={labFilter}
            onChange={(e) => setLabFilter(e.target.value)}
            className="text-sm px-3 py-2 border border-slate-200 rounded-md bg-white"
            data-testid="matrix-lab-filter"
          >
            <option value="">Filtrar por lab con descuento en molécula…</option>
            {data?.labs.map((l) => (
              <option key={l.laboratorio} value={l.laboratorio}>
                {l.laboratorio}
              </option>
            ))}
          </select>
          <label className="inline-flex items-center gap-2 text-xs text-slate-700 cursor-pointer select-none">
            <input type="checkbox" checked={onlyOpportunities} onChange={(e) => setOnlyOpportunities(e.target.checked)} className="rounded" data-testid="matrix-only-upside"/>
            Solo con oportunidad €
          </label>
          <label className="inline-flex items-center gap-2 text-xs text-slate-700 cursor-pointer select-none">
            <input type="checkbox" checked={onlyUncovered} onChange={(e) => setOnlyUncovered(e.target.checked)} className="rounded" data-testid="matrix-only-uncovered"/>
            Solo sin cobertura
          </label>
          <div className="ml-auto flex items-center gap-4 text-xs">
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-emerald-50 text-emerald-800 rounded-full border border-emerald-200">
              <TrendingUp className="h-3.5 w-3.5" />
              <span className="font-bold">Upside total:</span>
              <span className="font-mono-num font-bold">{fmtEur(totalUpside)}</span>
            </div>
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-rose-50 text-rose-800 rounded-full border border-rose-200">
              <AlertTriangle className="h-3.5 w-3.5" />
              <span className="font-bold">Sin cobertura:</span>
              <span className="font-mono-num font-bold">{uncoveredCount}</span>
            </div>
          </div>
        </div>

        {/* Body / Matrix */}
        <div className="flex-1 overflow-auto">
          {loading && (
            <div className="p-12 text-center text-slate-500 inline-flex items-center gap-2 justify-center w-full">
              <Loader2 className="h-4 w-4 animate-spin" /> Cargando matriz…
            </div>
          )}
          {error && (
            <div className="p-12 text-center text-rose-600" data-testid="matrix-error">{error}</div>
          )}
          {data && !loading && (
            <table className="text-xs border-collapse" data-testid="matrix-table">
              <thead className="sticky top-0 z-20 bg-slate-900 text-white">
                <tr>
                  <th className="text-left px-3 py-2 sticky left-0 z-30 bg-slate-900 min-w-[260px] max-w-[260px] border-r border-slate-700">
                    Molécula (G.H.)
                  </th>
                  <th className="text-right px-2 py-2 whitespace-nowrap border-r border-slate-700">€ mes</th>
                  <th className="text-center px-2 py-2 whitespace-nowrap border-r border-slate-700">Actual %</th>
                  <th className="text-left px-2 py-2 whitespace-nowrap border-r border-slate-700">Mejor lab</th>
                  <th className="text-right px-2 py-2 whitespace-nowrap border-r border-slate-700 text-emerald-300">Upside €</th>
                  {data.labs.map((l) => (
                    <th key={l.laboratorio} className="px-2 py-2 text-center border-r border-slate-700 min-w-[80px] whitespace-nowrap">
                      <div className="flex flex-col items-center gap-0.5">
                        <span className="max-w-[90px] truncate" title={l.laboratorio}>{l.laboratorio}</span>
                        {l.has_agreement && (
                          <span className="text-[8px] uppercase tracking-wider font-bold text-emerald-300 inline-flex items-center gap-0.5">
                            <CheckCircle2 className="h-2.5 w-2.5" /> conv
                          </span>
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredMolecules.length === 0 && (
                  <tr>
                    <td colSpan={5 + data.labs.length} className="text-center py-12 text-slate-400">
                      No hay moléculas con esos filtros.
                    </td>
                  </tr>
                )}
                {filteredMolecules.map((m, idx) => (
                  <tr key={m.gh} className={idx % 2 === 0 ? "bg-white" : "bg-slate-50/50"}>
                    <td
                      className={`px-3 py-1.5 sticky left-0 z-10 border-r border-slate-200 min-w-[260px] max-w-[260px] font-medium truncate ${idx % 2 === 0 ? "bg-white" : "bg-slate-50"}`}
                      title={m.gh}
                    >
                      {m.gh}
                    </td>
                    <td className="px-2 py-1.5 text-right font-mono-num border-r border-slate-200 whitespace-nowrap">
                      {fmtEur(m.eur_per_month)}
                    </td>
                    <td className="px-2 py-1.5 text-center font-mono-num border-r border-slate-200">
                      {m.current_pct > 0 ? fmtPct(m.current_pct) : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-2 py-1.5 border-r border-slate-200 max-w-[140px] truncate" title={m.best_lab || ""}>
                      {m.best_lab ? (
                        <span className="inline-flex items-center gap-1 text-emerald-700 font-semibold">
                          {m.best_lab} · {fmtPct(m.best_pct)}
                        </span>
                      ) : <span className="text-slate-300">Sin oferta</span>}
                    </td>
                    <td className="px-2 py-1.5 text-right font-mono-num border-r border-slate-200 whitespace-nowrap">
                      {m.upside_eur > 0 ? (
                        <span className="text-emerald-700 font-bold">+{fmtEur(m.upside_eur)}</span>
                      ) : <span className="text-slate-300">—</span>}
                    </td>
                    {data.labs.map((l) => {
                      const cell = m.cells[l.laboratorio];
                      const pct = cell?.discount_pct || 0;
                      const isBest = m.best_lab === l.laboratorio && pct > 0;
                      const isCurrent = cell?.is_current;
                      let cls = "text-slate-300";
                      if (pct > 0) cls = "text-slate-800 font-semibold";
                      if (isBest) cls = "bg-emerald-100 text-emerald-800 font-bold";
                      else if (isCurrent && pct > 0) cls = "bg-sky-100 text-sky-800 font-semibold";
                      else if (isCurrent) cls = "bg-sky-50 text-sky-700";
                      const title = [
                        `${l.laboratorio}`,
                        pct > 0 ? `Descuento ${fmtPct(pct)} (${cell.source})` : "Sin descuento configurado",
                        isCurrent ? `Comprado: ${fmtEur(cell.current_eur)}` : "",
                        cell?.in_catalog ? "Presente en catálogo" : "",
                      ].filter(Boolean).join(" · ");
                      return (
                        <td
                          key={l.laboratorio}
                          className={`px-2 py-1.5 text-center border-r border-slate-100 font-mono-num ${cls}`}
                          title={title}
                        >
                          {pct > 0 ? fmtPct(pct) : (isCurrent ? "•" : "")}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Legend */}
        <div className="border-t border-slate-200 px-6 py-2 text-[11px] text-slate-500 flex flex-wrap gap-4 items-center flex-shrink-0">
          <span className="inline-flex items-center gap-1.5">
            <span className="w-3 h-3 rounded bg-emerald-100 border border-emerald-300 inline-block"/>
            Mejor descuento disponible
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="w-3 h-3 rounded bg-sky-100 border border-sky-300 inline-block"/>
            Le compras hoy
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="w-3 h-3 rounded bg-white border border-slate-300 inline-block"/>
            Con descuento configurado
          </span>
          <span className="ml-auto">La celda muestra el % de descuento aplicable · Hover para más detalle</span>
        </div>
      </div>
    </div>
  );
}
